import React, { useState } from 'react';
import { FileText, ChevronDown, ChevronUp, Sparkles, AlertTriangle, ExternalLink } from 'lucide-react';

/**
 * SupplementalPromptDisplay - Shows the essay prompt prominently
 * and strategic context as collapsible reference
 *
 * Used during Topic Development for supplemental essays
 */
export default function SupplementalPromptDisplay({
  prompt,
  wordLimit,
  schoolName,
  supplementalContext,
  showStrategicContext = false,
  contextType = 'reference_only', // 'reference_only' | 'full_uvp' | 'light'
  currentPhase, // Current phase of development
  onProgramVerification
}) {
  const [uvpExpanded, setUvpExpanded] = useState(false);
  const [programsExpanded, setProgramsExpanded] = useState(false);

  // Use classification for enhanced interpretation if available
  const classification = supplementalContext?.classification;

  const handleProgramVerification = (programName, verificationData) => {
    // Update supplemental context with verification status
    if (onProgramVerification) {
      onProgramVerification(programName, verificationData);
    }
  };

  return (
    <div className="space-y-4 mb-6">
      {/* THE PROMPT - Always visible, prominent */}
      <div className="bg-gradient-to-r from-indigo-50 to-violet-50 border-2 border-indigo-200 rounded-xl p-6">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-indigo-900 uppercase tracking-wide">
                The Prompt
              </h3>
              {wordLimit && (
                <span className="text-sm font-bold text-indigo-600">
                  {wordLimit} words
                </span>
              )}
            </div>
            <p className="text-lg font-semibold text-slate-900 leading-relaxed">
              "{prompt}"
            </p>
          </div>
        </div>

        {/* What they're really asking */}
        <div className="bg-white border border-indigo-100 rounded-lg p-4 mt-4">
          <h4 className="text-xs font-bold text-indigo-900 uppercase tracking-wide mb-2">
            What they want to know:
          </h4>
          {classification ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-700 italic">
                {getPromptInterpretation(classification.primary_type, schoolName)}
              </p>
              {classification.key_elements && classification.key_elements.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1">Key elements to address:</p>
                  <ul className="space-y-1 text-sm text-slate-700">
                    {classification.key_elements.map((element, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-indigo-600 mt-0.5">•</span>
                        <span>{element}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <ul className="space-y-1 text-sm text-slate-700">
              {getPromptPurpose(prompt).map((purpose, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-indigo-600 mt-0.5">•</span>
                  <span>{purpose}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Strategic Context - Collapsible, Optional */}
      {showStrategicContext && supplementalContext && (
        <div className="space-y-3">
          {/* UVP Reference */}
          {(contextType === 'reference_only' || contextType === 'full_uvp') && supplementalContext.uvp && (
            <div className="bg-violet-50 border border-violet-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setUvpExpanded(!uvpExpanded)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-violet-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-600" />
                  <span className="text-sm font-bold text-violet-900">
                    Strategic Positioning (Reference Only)
                  </span>
                </div>
                {uvpExpanded ? (
                  <ChevronUp className="w-4 h-4 text-violet-600" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-violet-600" />
                )}
              </button>

              {uvpExpanded && (
                <div className="px-4 pb-4 space-y-3">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-900">
                      <strong>Important:</strong> This is strategic framing to guide your thinking -
                      NOT essay language to copy. Use your own authentic voice.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xs font-bold text-violet-900 uppercase tracking-wide mb-2">
                      Your Core Value Proposition
                    </h4>
                    <p className="text-sm text-violet-800 leading-relaxed italic">
                      {supplementalContext.uvp.core_value_proposition}
                    </p>
                  </div>

                  {supplementalContext.uvp.why_school_needs_you && supplementalContext.uvp.why_school_needs_you.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-violet-900 uppercase tracking-wide mb-2">
                        Why {schoolName} Needs You
                      </h4>
                      <ul className="space-y-2">
                        {supplementalContext.uvp.why_school_needs_you.map((item, i) => (
                          <li key={i} className="text-sm text-violet-800 flex items-start gap-2">
                            <span className="text-violet-600 font-bold mt-0.5">{i + 1}.</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Programs to Explore */}
          {supplementalContext.programs && supplementalContext.programs.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setProgramsExpanded(!programsExpanded)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-blue-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-bold text-blue-900">
                    Programs to Explore ({supplementalContext.programs.length})
                  </span>
                </div>
                {programsExpanded ? (
                  <ChevronUp className="w-4 h-4 text-blue-600" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-blue-600" />
                )}
              </button>

              {programsExpanded && (
                <div className="px-4 pb-4 space-y-3">
                  {supplementalContext.programs.map((program, i) => (
                    <ProgramCard
                      key={i}
                      program={program}
                      onVerificationChange={handleProgramVerification}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Program verification card
 */
function ProgramCard({ program, onVerificationChange }) {
  const [verified, setVerified] = useState(program.verified || false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationUrl, setVerificationUrl] = useState(program.verification_url || '');

  return (
    <div className="bg-white border border-blue-200 rounded-lg p-3">
      <div className="flex items-start gap-2 mb-2">
        <div className={`px-2 py-0.5 rounded text-xs font-bold ${
          verified
            ? 'bg-emerald-100 text-emerald-700'
            : 'bg-amber-100 text-amber-700'
        }`}>
          {verified ? '✓ Verified' : '⚠️ Needs Verification'}
        </div>
        <h4 className="text-sm font-bold text-slate-900 flex-1">
          {program.name}
        </h4>
      </div>

      {program.description && (
        <p className="text-xs text-slate-600 mb-2">
          {program.description}
        </p>
      )}

      {program.connection && (
        <p className="text-xs text-blue-700 bg-blue-50 rounded px-2 py-1 mb-2">
          <strong>Why it might fit:</strong> {program.connection}
        </p>
      )}

      {!verified && (
        <div className="space-y-2">
          <button
            onClick={() => setShowVerification(!showVerification)}
            className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" />
            {showVerification ? 'Hide verification' : 'Verify this program'}
          </button>

          {showVerification && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2">
              <p className="text-xs font-bold text-blue-900 mb-2">
                Before referencing this program, verify:
              </p>
              <label className="flex items-start gap-2 text-xs">
                <input type="checkbox" className="mt-0.5" />
                <span>I've visited the program website and confirmed it exists</span>
              </label>
              <label className="flex items-start gap-2 text-xs">
                <input type="checkbox" className="mt-0.5" />
                <span>I've verified the program details are current</span>
              </label>
              {program.faculty && (
                <label className="flex items-start gap-2 text-xs">
                  <input type="checkbox" className="mt-0.5" />
                  <span>I've confirmed {program.faculty} is still affiliated</span>
                </label>
              )}
              <input
                type="url"
                placeholder="Program URL (for your reference)"
                value={verificationUrl}
                onChange={(e) => setVerificationUrl(e.target.value)}
                className="w-full px-2 py-1 text-xs border border-blue-300 rounded"
              />
              <button
                onClick={() => {
                  setVerified(true);
                  setShowVerification(false);
                  // Notify parent of verification
                  if (onVerificationChange) {
                    onVerificationChange(program.name, {
                      verified: true,
                      verification_url: verificationUrl,
                      verified_at: new Date().toISOString()
                    });
                  }
                }}
                className="w-full px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700"
              >
                Mark as Verified
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Helper to get interpretation based on classification type
 */
function getPromptInterpretation(promptType, schoolName) {
  const interpretations = {
    WHY_MAJOR: `They want to understand your genuine connection to this field and why ${schoolName}'s specific approach is the right fit for YOU.`,
    WHY_SCHOOL_GENERAL: `They're looking for evidence that you've researched what makes ${schoolName} distinctive and how that matches your learning style and goals.`,
    CHALLENGE_OBSTACLE: `They want to see HOW you think and problem-solve when facing difficulty - not just that you overcame something.`,
    CREATIVE_INTELLECTUAL: `They're giving you space to show intellectual playfulness while revealing something substantive about how your mind works.`,
    IDENTITY_DIVERSITY: `They want to understand how a specific aspect of your identity shapes your perspective and what unique viewpoint you'll bring.`,
    VALUES_BELIEFS: `They're looking for evidence of your values in action through specific choices and moments, not abstract statements.`,
    COMMUNITY_BELONGING: `They want to see how you've contributed to a community and what that experience taught you about yourself.`,
    ACADEMIC_APPROACH: `They're curious about your learning style and intellectual curiosity - how you engage with ideas, not just what you want to study.`,
    OPEN_ENDED_REVEAL: `They're giving you space to share something unexpected that reveals a different dimension of who you are.`
  };

  return interpretations[promptType] || `They want authentic insight into who you are and how you think.`;
}

/**
 * Helper to determine what the prompt is really asking (fallback when no classification)
 */
function getPromptPurpose(prompt) {
  const promptLower = prompt.toLowerCase();

  // Why [School]? prompts
  if (promptLower.includes('why') && (promptLower.includes('university') || promptLower.includes('college') || promptLower.includes('school'))) {
    return [
      'Do you know what makes us different from other schools?',
      'Have you done genuine research beyond our website homepage?',
      'Would you actually thrive in our specific environment?',
      'Can you articulate a clear connection between you and us?'
    ];
  }

  // Community prompts
  if (promptLower.includes('community') || promptLower.includes('belong')) {
    return [
      'What communities have shaped who you are?',
      'How do you show up for others in a community?',
      'What kind of community would you help build here?'
    ];
  }

  // Intellectual curiosity prompts
  if (promptLower.includes('intellectual') || promptLower.includes('curious') || promptLower.includes('idea')) {
    return [
      'What genuinely fascinates you intellectually?',
      'How do you engage with ideas outside the classroom?',
      'What would you bring to our intellectual community?'
    ];
  }

  // Challenge/Obstacle prompts
  if (promptLower.includes('challenge') || promptLower.includes('obstacle') || promptLower.includes('setback')) {
    return [
      'How do you handle adversity and setbacks?',
      'What have challenges taught you about yourself?',
      'How have you grown through difficulty?'
    ];
  }

  // Generic fallback
  return [
    'What does this reveal about who you are?',
    'How does this connect to your values and goals?',
    'What will you bring to our campus?'
  ];
}
